import { Box, Grow } from "@mui/material";
import { Components } from "@mui/material/styles";
import { SxProps, Theme } from "@mui/material/styles";
import { themeShadows } from "src/theme-mui/tokens/shadows";
import { CSS_CLASS_NAME_CHECKBOX_ICON, CSS_CLASS_NAME_CHECKBOX_ICON_SVG } from "./constants";

type BaseBoxProps = React.PropsWithChildren<{
    sx?: SxProps<Theme>;
}>;

const BaseBox = ({ sx = {}, children }: BaseBoxProps) => (
    <Box
        component="span"
        className={CSS_CLASS_NAME_CHECKBOX_ICON}
        sx={{
            height: "24px",
            width: "24px",
            display: "inline-block",
            border: "1px solid",
            borderColor: "var(--mui-palette-gray-400)",
            borderRadius: 0.5,
            boxSizing: "border-box",
            transition: "border-color 0.2s",
            ...sx,
        }}
    >
        {children}
    </Box>
);

const CheckedIcon = () => (
    <BaseBox
        sx={{
            height: 24,
            width: 24,
            display: "flex",
            border: "1px solid",
            borderColor: "var(--mui-palette-gray-800)",
            borderRadius: "4px",
            backgroundColor: "var(--mui-palette-gray-800)",
            boxSizing: "border-box",
            position: "relative",
            color: "white",
            alignItems: "center",
            justifyContent: "center",
        }}
    >
        <Grow in={true} timeout={250} style={{ transformOrigin: "bottom center" }}>
            <svg
                className={CSS_CLASS_NAME_CHECKBOX_ICON_SVG}
                viewBox="0 0 12 10"
                style={{
                    fill: "none",
                    strokeWidth: 2,
                    stroke: "currentcolor",
                    strokeDasharray: 16,
                }}
            >
                <polyline points="1.5 6 4.5 9 10.5 1"></polyline>
            </svg>
        </Grow>
    </BaseBox>
);

export const MuiCheckbox: Components["MuiCheckbox"] = {
    defaultProps: {
        icon: <BaseBox />,
        checkedIcon: <CheckedIcon />,
        disableRipple: true,
        size: "medium",
        color: "gray",
    },
    styleOverrides: {
        root: ({ ownerState }) => {
            const isGray = ownerState.color === "gray" || ownerState.color === undefined;
            const backgroundColor = isGray ? "var(--mui-palette-gray-800)" : `var(--mui-palette-${ownerState.color}-600)`;

            return {
                color: "var(--mui-palette-gray-600)",
                transition: "background-color 0.3s ease, border-color 0.25s, color 0.25s",
                margin: 0,
                padding: 0,

                [`&.Mui-checked .${CSS_CLASS_NAME_CHECKBOX_ICON}`]: {
                    backgroundColor,
                    borderColor: backgroundColor,
                },
                [`.${CSS_CLASS_NAME_CHECKBOX_ICON_SVG}`]: {
                    width: "18px",
                    height: "15px",
                },

                [`&:hover:not(.Mui-checked):not(.Mui-disabled) .${CSS_CLASS_NAME_CHECKBOX_ICON}`]: {
                    borderColor: "var(--mui-palette-gray-800)",
                },
                [`&:hover.Mui-checked:not(.Mui-disabled) .${CSS_CLASS_NAME_CHECKBOX_ICON}`]: {
                    borderColor: "var(--mui-palette-gray-400)",
                },

                [`&.Mui-focusVisible .${CSS_CLASS_NAME_CHECKBOX_ICON}`]: {
                    borderColor: "var(--mui-palette-gray-400)",
                    boxShadow: themeShadows.focusVisible,
                },

                [`&.Mui-disabled .${CSS_CLASS_NAME_CHECKBOX_ICON}`]: {
                    backgroundColor: "var(--mui-palette-gray-100)",
                    borderColor: "var(--mui-palette-gray-100)",
                    cursor: "not-allowed",
                },
                [`&.Mui-disabled.Mui-checked .${CSS_CLASS_NAME_CHECKBOX_ICON}`]: {
                    backgroundColor: "var(--mui-palette-gray-100)",
                    borderColor: "var(--mui-palette-gray-100)",
                    color: "var(--mui-palette-gray-200)",
                },
            };
        },

        sizeSmall: {
            padding: 0,
            [`.${CSS_CLASS_NAME_CHECKBOX_ICON}`]: {
                height: "16px",
                width: "16px",
                borderRadius: "2px",
            },
            [`.${CSS_CLASS_NAME_CHECKBOX_ICON_SVG}`]: {
                height: "12px",
                width: "10px",
            },
        },
    },
};
